import { analyticsBlocked, analyticsPageContext, isPrivateAnalyticsUrl, sanitizeAnalyticsParams, sanitizeAnalyticsUrl } from './analyticsPrivacy';
/**
 * Google Analytics 4 (GA4) tracking utilities
 * Integrated with Google Consent Mode for GDPR/CCPA compliance
 */
import type { Metric } from 'web-vitals';

const GA_MEASUREMENT_ID = 'G-SJF1CP3VGH';
const COOKIE_CONSENT_STORAGE_KEY = 'haven_cookie_consent';
const MIXPANEL_DISTINCT_ID_STORAGE_KEY = 'haven_mixpanel_distinct_id';
const DEFAULT_MIXPANEL_API_HOST = 'https://api-js.mixpanel.com';

interface AnalyticsConsentState {
  preferences?: {
    analytics?: boolean;
  };
}

export interface AnalyticsItem {
  item_id: string;
  item_name: string;
  item_variant?: string;
  item_brand?: string;
  item_category?: string;
  price?: number;
  quantity?: number;
}

const getMixpanelToken = (): string | undefined => {
  return import.meta.env.VITE_MIXPANEL_TOKEN || import.meta.env.VITE_MIXPANEL_ID;
};

const getMixpanelApiHost = (): string | undefined => {
  return import.meta.env.VITE_MIXPANEL_API_HOST;
};

const encodeBase64Utf8 = (value: string): string => {
  const bytes = new TextEncoder().encode(value);
  let binary = '';

  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });

  return window.btoa(binary);
};

const getMixpanelDistinctId = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const existingDistinctId = window.localStorage.getItem(MIXPANEL_DISTINCT_ID_STORAGE_KEY);
    if (existingDistinctId) {
      return existingDistinctId;
    }

    const generatedDistinctId = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `haven-${Date.now()}-${Math.random().toString(16).slice(2)}`;

    window.localStorage.setItem(MIXPANEL_DISTINCT_ID_STORAGE_KEY, generatedDistinctId);
    return generatedDistinctId;
  } catch (error) {
    console.error('Error creating Mixpanel distinct id:', error);
    return null;
  }
};

const getAnalyticsConsent = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    const storedConsent = window.localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);

    if (!storedConsent) {
      return false;
    }

    const parsedConsent = JSON.parse(storedConsent) as AnalyticsConsentState;
    return Boolean(parsedConsent.preferences?.analytics);
  } catch (error) {
    console.error('Error reading analytics consent:', error);
    return false;
  }
};

export const initMixpanel = async (): Promise<boolean> => {
  if (typeof window === 'undefined') {
    return false;
  }

  const token = getMixpanelToken();
  if (!token) {
    return false;
  }

  return true;
};

const sendMixpanelEvent = async (
  eventName: string,
  eventParams?: Record<string, any>
): Promise<void> => {
  if (typeof window === 'undefined' || analyticsBlocked() || !getAnalyticsConsent()) {
    return;
  }

  const token = getMixpanelToken();
  if (!token) {
    return;
  }

  const distinctId = getMixpanelDistinctId();
  if (!distinctId) {
    return;
  }

  const properties = sanitizeEventParams({
    token,
    distinct_id: distinctId,
    $insert_id: typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    time: Math.floor(Date.now() / 1000),
    platform: 'website',
    page_url: sanitizeAnalyticsUrl(window.location.href),
    ...eventParams,
  });

  const payload = {
    event: eventName,
    properties: { ...properties, token },
  };

  const apiHost = getMixpanelApiHost() || DEFAULT_MIXPANEL_API_HOST;

  try {
    const response = await fetch(`${apiHost}/track/?verbose=1`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: `data=${encodeURIComponent(encodeBase64Utf8(JSON.stringify(payload)))}`,
    });

    if (!response.ok) {
      console.error('Mixpanel track request failed:', response.status, response.statusText);
      return;
    }
    await response.text();
  } catch (error) {
    console.error('Error sending Mixpanel event:', error);
  }
};

const sanitizeEventParams = (eventParams?: Record<string, any>): Record<string, any> | undefined => {
  if (!eventParams) {
    return undefined;
  }

  return sanitizeAnalyticsParams(eventParams);
};

/**
 * Check if GA4 is loaded and available
 */
export const isGA4Available = (): boolean => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function' && typeof window.dataLayer !== 'undefined';
};

export const isMixpanelAvailable = (): boolean => Boolean(getMixpanelToken());

/**
 * Wait for gtag to be available (with timeout)
 */
export const waitForGA4 = (maxWait: number = 5000): Promise<boolean> => {
  return new Promise((resolve) => {
    if (isGA4Available()) {
      resolve(true);
      return;
    }

    const startTime = Date.now();
    const checkInterval = setInterval(() => {
      if (isGA4Available()) {
        clearInterval(checkInterval);
        resolve(true);
      } else if (Date.now() - startTime > maxWait) {
        clearInterval(checkInterval);
        console.warn('GA4 not available after', maxWait, 'ms');
        resolve(false);
      }
    }, 100);
  });
};

/**
 * Track a page view
 * @param path - The page path (e.g., '/about', '/contact')
 * @param title - Optional page title
 */
export const trackPageView = async (path: string, title?: string): Promise<void> => {
  if (analyticsBlocked() || isPrivateAnalyticsUrl(path)) return;
  const safePath = sanitizeAnalyticsUrl(path, true);
  const pageTitle = title || document.title;

  const gaAvailable = await waitForGA4();
  if (analyticsBlocked()) return;
  if (gaAvailable) {
    try {
      window.gtag('config', GA_MEASUREMENT_ID, {
        page_title: pageTitle,
        ...analyticsPageContext(),
        page_path: safePath,
      });
    } catch (error) {
      console.error('Error tracking GA4 page view:', error);
    }
  } else {
    console.warn('GA4 not available - gtag function not found');
  }

  if (!getAnalyticsConsent()) {
    return;
  }

  await sendMixpanelEvent('ws_page_view', {
    page_path: safePath,
    page_title: pageTitle,
  });
};

/**
 * Track a custom event
 * @param eventName - The event name (e.g., 'button_click', 'form_submit')
 * @param eventParams - Optional event parameters
 */
export const trackEvent = (
  eventName: string,
  eventParams?: Record<string, any>
): void => {
  if (analyticsBlocked()) return;
  const sanitizedParams = { ...sanitizeEventParams(eventParams), ...analyticsPageContext() };

  if (isGA4Available()) {
    window.gtag('event', eventName, sanitizedParams);
  }

  if (getAnalyticsConsent()) {
    void sendMixpanelEvent(`ws_${eventName}`, sanitizedParams);
  }
};

/**
 * Update consent state (called when user accepts/rejects cookies)
 * @param analyticsGranted - Whether analytics consent is granted
 * @param marketingGranted - Whether marketing consent is granted (defaults to analyticsGranted if not provided)
 */
export const updateConsentState = (analyticsGranted: boolean, marketingGranted?: boolean): void => {
  if (typeof window === 'undefined') return;

  // Wait for gtag to be available
  waitForGA4().then((isAvailable) => {
    if (!isAvailable) {
      console.warn('GA4 not available when updating consent state');
      return;
    }

    // If marketingGranted is not provided, default to analyticsGranted
    const marketingValue = marketingGranted !== undefined ? marketingGranted : analyticsGranted;

    const analyticsValue = analyticsGranted ? 'granted' : 'denied';
    const adValue = marketingValue ? 'granted' : 'denied';

    try {
      window.gtag('consent', 'update', {
        analytics_storage: analyticsValue,
        ad_storage: adValue,
        ad_user_data: adValue,
        ad_personalization: adValue,
      });
    } catch (error) {
      console.error('Error updating consent state:', error);
    }
  });
};

/**
 * Common event tracking helpers
 */
export const analytics = {
  // E-commerce events
  trackPurchase: (
    transactionId: string,
    value: number,
    currency: string = 'USD',
    items: AnalyticsItem[] = [],
    additionalParams?: Record<string, any>
  ) => {
    trackEvent('purchase', {
      transaction_id: transactionId,
      value,
      currency,
      items,
      ...additionalParams,
    });
  },

  trackProductView: (item: AnalyticsItem, currency: string = 'USD', location?: string) => {
    trackEvent('view_item', {
      currency,
      value: item.price,
      item_name: item.item_name,
      location,
      items: [item],
    });
  },

  trackAddToCart: (item: AnalyticsItem, currency: string = 'USD', additionalParams?: Record<string, any>) => {
    trackEvent('add_to_cart', {
      currency,
      value: item.price,
      item_name: item.item_name,
      items: [item],
      ...additionalParams,
    });
  },

  trackBeginCheckout: (
    items: AnalyticsItem[],
    value?: number,
    currency: string = 'USD',
    additionalParams?: Record<string, any>
  ) => {
    trackEvent('begin_checkout', {
      currency,
      value,
      items,
      item_count: items.reduce((count, item) => count + (item.quantity || 0), 0),
      ...additionalParams,
    });
  },

  // Engagement events
  trackButtonClick: (buttonName: string, location?: string) => {
    trackEvent('button_click', {
      button_name: buttonName,
      location,
    });
  },

  trackFormSubmit: (formName: string) => {
    trackEvent('form_submit', {
      form_name: formName,
    });
  },

  trackDownload: (fileName: string, fileType?: string) => {
    trackEvent('file_download', {
      file_name: fileName,
      file_type: fileType,
    });
  },

  trackOutboundLink: (url: string) => {
    trackEvent('click', {
      event_category: 'outbound',
      event_label: url,
      transport_type: 'beacon',
    });
  },

  trackSegmentSelection: (segment: string, location: string, destination?: string, additionalParams?: Record<string, any>) => {
    trackEvent('segment_selection', {
      segment,
      location,
      destination,
      ...additionalParams,
    });
  },

  trackProLeadStart: (entryPoint: string, location: string, destination?: string, additionalParams?: Record<string, any>) => {
    trackEvent('pro_lead_start', {
      entry_point: entryPoint,
      location,
      destination,
      ...additionalParams,
    });
  },

  trackAppDownloadClick: (platform: 'ios' | 'android', location: string, destination: string) => {
    trackEvent('app_download_click', {
      platform,
      location,
      destination,
    });
  },

  // Custom events
  trackCustom: (eventName: string, params?: Record<string, any>) => {
    trackEvent(eventName, params);
  },
};

export const trackWebVital = (metric: Metric): void => {
  const eventValue = metric.name === 'CLS' ? Math.round(metric.value * 1000) : Math.round(metric.value);

  trackEvent('web_vital', {
    event_category: 'Web Vitals',
    event_label: metric.name,
    value: eventValue,
    metric_id: metric.id,
    metric_name: metric.name,
    metric_delta: metric.delta,
    metric_value: metric.value,
    metric_rating: metric.rating,
    non_interaction: true,
  });
};
